/**
 * Send test for one webhook stream link (ENG-2832, ENG-2427 item 4).
 *
 * `POST webhook/{linkId}/test/` queues one test detection for that link only
 * and answers 202 with a test row; the consumer reports what the receiver did
 * and admin completes the row. `GET webhook/{linkId}/tests/` lists rows newest
 * first. A row still pending after 120 s comes back as failed, so polling
 * always ends. Only the production admin sends tests: other stages answer 409.
 */

import { AxiosError } from "axios";
import { createApi } from "../../../../utils/createApi";

export type WebhookTestResult = "pending" | "delivered" | "failed";

export interface WebhookTestRow {
  id: number;
  webhook_stream_id: number;
  event_type: string;
  sent_at: string;
  result: WebhookTestResult;
  status_code: number | null;
  detail: string | null;
  duration_ms: number | null;
  attempts: number | null;
  completed_at: string | null;
}

/** What the button shows once a test ends (or could not start). */
export type WebhookTestOutcome =
  | { kind: "delivered"; message: string }
  | { kind: "failed"; message: string }
  | { kind: "cooldown"; message: string; retryAfterSeconds: number }
  | { kind: "error"; message: string };

export const POLL_INTERVAL_MS = 2000;
/** Server gives up at 120 s; stop a little after that in case a poll is slow. */
export const POLL_TIMEOUT_MS = 130000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const outcomeForRow = (row: WebhookTestRow): WebhookTestOutcome => {
  if (row.result === "delivered") {
    return {
      kind: "delivered",
      message:
        row.detail ||
        `Delivered${row.status_code ? ` (HTTP ${row.status_code})` : ""}`,
    };
  }
  return {
    kind: "failed",
    message:
      row.detail ||
      (row.status_code ? `HTTP ${row.status_code} from receiver` : "Failed"),
  };
};

/** Turn a refused POST into something the button can show. */
export const outcomeForError = (reason: unknown): WebhookTestOutcome => {
  const response = (reason as AxiosError<any>)?.response;
  const status = response?.status;
  const error: string | undefined = response?.data?.error;
  if (status === 429) {
    const header = Number(response?.headers?.["retry-after"]);
    const body = Number(response?.data?.retry_after_seconds);
    const retryAfterSeconds = Number.isFinite(body)
      ? body
      : Number.isFinite(header)
      ? header
      : 60;
    return {
      kind: "cooldown",
      retryAfterSeconds,
      message: `A test was just sent. Try again in ${retryAfterSeconds} s.`,
    };
  }
  if (status === 403) {
    return {
      kind: "error",
      message: error || "You don't have permission to send webhook tests.",
    };
  }
  return {
    kind: "error",
    message: error || "Couldn't send the test. Please try again.",
  };
};

/**
 * Send one test and wait for its result. Never throws: refusals, network
 * errors and timeouts all come back as an outcome.
 */
export const runWebhookTest = async (
  linkId: number,
  {
    wait = sleep,
    now = () => Date.now(),
  }: { wait?: (ms: number) => Promise<unknown>; now?: () => number } = {}
): Promise<WebhookTestOutcome> => {
  const AxiosUI = createApi("");
  let row: WebhookTestRow;
  try {
    const response = await AxiosUI.post(`webhook/${linkId}/test/`, {
      event_type: "detection",
    });
    row = response.data;
  } catch (reason) {
    return outcomeForError(reason);
  }
  const startedAt = now();
  while (row.result === "pending") {
    if (now() - startedAt > POLL_TIMEOUT_MS) {
      return {
        kind: "failed",
        message: "No result yet. Check the receiver, then try again.",
      };
    }
    await wait(POLL_INTERVAL_MS);
    try {
      const response = await AxiosUI.get(`webhook/${linkId}/tests/?limit=5`);
      const rows: WebhookTestRow[] = Array.isArray(response.data)
        ? response.data
        : [];
      const latest = rows.find((candidate) => candidate.id === row.id);
      if (latest) row = latest;
    } catch {
      // A failed poll is retried on the next tick until the timeout.
    }
  }
  return outcomeForRow(row);
};
